import _ from 'lodash'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks'
// import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useVideoRecorder } from '@/hooks/useVideoRecorder'

import type { Theme, SxProps } from '@mui/material/styles'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Fab from '@mui/material/Fab'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import CameraIcon from '@mui/icons-material/Camera'
import CameraswitchOutlinedIcon from '@mui/icons-material/CameraswitchOutlined'
// import CancelPresentationOutlinedIcon from '@mui/icons-material/CancelPresentationOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
// import PausePresentationOutlinedIcon from '@mui/icons-material/PausePresentationOutlined'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import ScreenShareOutlinedIcon from '@mui/icons-material/ScreenShareOutlined'
import SettingsIcon from '@mui/icons-material/Settings'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import StopScreenShareOutlinedIcon from '@mui/icons-material/StopScreenShareOutlined'

import colors from '@/assets/scss/_themes-vars.module.scss'

import DeviceSelectionDialog from '@/components/ui/dialogs/DeviceSelectionDialog'
// import { VideoFreezeDialog } from '@/components/ui/dialogs/VideoFreezeDialog'

import { Consultation } from '@/core/models/consultation.model'
import { getFileCount } from '@/utils/consultations'
import { useDebouncedCallback } from 'use-debounce'
import ConnectionIndicator from '@/components/ui/meeting/ConnectedIndicator'
import { usePeer } from '@/providers/PeerjsProvider'

dayjs.extend(duration)

/**
 * I know we should not use usePeer here, but i couldn't put it well when working with rendervideo() only
 * So i hide it if there is no peer connected
 * @returns
 */
const CnxIndicator = () => {
  const { connectedPeers } = usePeer()
  if (connectedPeers.length === 0) return null
  return <ConnectionIndicator connectedPeers={connectedPeers} />
}

export type MedicalVideoProps = {
  disableConnectionIndicator?: boolean
  isScreenSharing?: boolean
  isStreamer: boolean
  medicalCameras?: Array<{
    id: string
    device: {
      deviceId: string
      label: string
      kind: MediaDeviceKind
    }
    customName: string
    iconURL: string
    type: string
    resolutions?: string[]
  }>
  onRecordStop?: (file: File) => void
  onScreenshot?: () => void
  onSaveFreezedPhoto?: (photo: File) => void
  onSaveFreezedVideo?: (video: File) => void
  onSelectDevice?: (deviceId: string) => void
  onSelectResolution?: (deviceId: string, resolution: string) => void
  onShareScreen?: (isScreenSharing: boolean) => void
  onStopVideo?: () => void
  renderVideo: () => JSX.Element
  selectedDeviceId?: string
  stream: MediaStream | null
  videoMediaDevices: Array<MediaDeviceInfo & { resolutions?: string[] }>
  consultation: Consultation
  videoHeight?: string
  videoWidth?: string
  sx?: SxProps<Theme>
  screenshotCount?: number
  videoRecordingCount?: number
}

export type MedicalVideoHandle = {
  /** Returns false if stream is not available */
  startRecording: () => boolean
  stopRecording: () => void
  isRecording: boolean
}

const autoStopRecordingMaxTime = 120 // change to two minutes max recording time

export const MedicalVideo = forwardRef<MedicalVideoHandle, MedicalVideoProps>(function MedicalVideo(props, ref) {
  const {
    disableConnectionIndicator = false,
    isScreenSharing,
    isStreamer,
    medicalCameras = [],
    onRecordStop,
    onScreenshot,
    // onSaveFreezedPhoto,
    // onSaveFreezedVideo,
    onSelectDevice,
    onSelectResolution,
    onShareScreen,
    // onStopVideo,
    renderVideo,
    selectedDeviceId,
    stream,
    videoMediaDevices,
    consultation,
    sx = [],
    screenshotCount,
    videoRecordingCount,
  } = props
  const videoParamsPopupState = usePopupState({ variant: 'popover', popupId: 'video-params' })
  const videoQualityPopupState = usePopupState({ variant: 'popover', popupId: 'video-quality' })
  // const { enqueueSnackbar } = useSnackbar()
  // const freezeRecorder = useVideoRecorder({
  //   duration: autoStopRecordingMaxTime,
  //   onRecordingStopped: ({ blob, isAutoStop }) => {
  //     if (!isAutoStop) {
  //       if (blob.size === 0) {
  //         enqueueSnackbar(t('notification.consultation.freezeVideo.error'), { variant: 'error' })
  //         return
  //       }
  //       setVideoFreezeSrcBlob(blob)
  //       setVideoFreezeDialogOpen(true)
  //     }
  //   },
  // })
  const videoRecorder = useVideoRecorder({
    duration: autoStopRecordingMaxTime,
    onRecordingStopped: ({ blob, name }) => {
      const videoDocument = new File([blob], `medical-${name}`, { type: 'video/webm' })
      onRecordStop?.(videoDocument)
    },
  })
  const { t } = useTranslation()

  const [deviceSelectionDialogOpen, setDeviceSelectionDialogOpen] = useState<boolean>(false)
  const [selectedResolution, setSelectedResolution] = useState<string>()
  // const [videoFreezeDialogOpen, setVideoFreezeDialogOpen] = useState<boolean>(false)
  // const [videoFreezeSrcBlob, setVideoFreezeSrcBlob] = useState<Blob>(new Blob())

  const videoInputDevices = useMemo(
    () =>
      _.concat(
        _.map(medicalCameras, (medicalCamera) => ({
          id: medicalCamera.device.deviceId,
          iconUrl: medicalCamera.iconURL,
          isCustom: true,
          isSelected: medicalCamera.device.deviceId === selectedDeviceId,
          label: medicalCamera.customName,
          type: medicalCamera.device.kind,
          resolutions: medicalCamera.resolutions,
        })),
        _.map(videoMediaDevices, (mediaDevice) => ({
          id: mediaDevice.deviceId,
          isCustom: false,
          isSelected: mediaDevice.deviceId === selectedDeviceId,
          label: mediaDevice.label,
          type: 'videoinput' as MediaDeviceKind,
          resolutions: mediaDevice.resolutions,
        }))
      ),
    [medicalCameras, videoMediaDevices, selectedDeviceId]
  )

  // TODO : fix error
  // start freeze when close video freeze dialog
  // useEffect(() => {
  //   if (!freezeRecorder.isRecording && stream && !!stream.getVideoTracks()[0] && !videoFreezeDialogOpen) {
  //     freezeRecorder.startRecording(stream)
  //   }
  //   // eslint-disable-next-line
  // }, [freezeRecorder.isRecording, stream, videoFreezeDialogOpen])

  // const handleFreezeVideo = () => {
  //   if (freezeRecorder.isRecording) {
  //     freezeRecorder.stopRecording()
  //   }
  // }

  const handleSelectDevice = useCallback(
    (deviceId: string) => {
      if (deviceId === selectedDeviceId) return
      setDeviceSelectionDialogOpen(false)
      onSelectDevice?.(deviceId)
    },
    [onSelectDevice, selectedDeviceId]
  )

  const handleStartShareScreen = () => {
    onShareScreen?.(true)
  }

  const handleStopShareScreen = () => {
    onShareScreen?.(false)
  }

  const handleStartRecording = () => {
    if (stream) videoRecorder.startRecording(stream)
  }

  const handleStopRecording = () => {
    videoRecorder.stopRecording()
  }

  useImperativeHandle(ref, () => ({
    startRecording: () => {
      if (!stream) return false
      videoRecorder.startRecording(stream)
      return true
    },
    stopRecording: handleStopRecording,
    isRecording: videoRecorder.isRecording,
  }))

  // const handleStopVideo = () => {
  //   onStopVideo?.()
  // }

  const toggleDeviceSelectionDialog = () => {
    setDeviceSelectionDialogOpen((prevState) => !prevState)
  }

  // const toggleVideoFreezeDialog = () => {
  //   setVideoFreezeDialogOpen((prevState) => !prevState)
  // }

  const selectedDevice = _.find(videoInputDevices, (device) => device.id === selectedDeviceId)

  // keyboard binding for screenshot and video recording
  // need to use deboucend callback because the event is fired multiple times
  // leave console log so we are sure that events are fired can be easily checked tahnk you
  const reactToKey = useDebouncedCallback((event: globalThis.KeyboardEvent) => {
    switch (true) {
      case event.key === 'p' && event.altKey:
        onScreenshot?.()
        console.log('DEBUG : screenshot')
        break
      case event.key === 'w' && event.altKey:
        if (videoRecorder.isRecording === false) handleStartRecording()
        else handleStopRecording()
        console.log('DEBUG : videorecording')
        break
      default:
        break
    }
  }, 500)

  useEffect(() => {
    window.addEventListener('keydown', reactToKey)
    return () => {
      window.removeEventListener('keydown', reactToKey)
    }
  }, [reactToKey])

  return (
    <Box
      data-testid="medical-video"
      sx={[
        {
          width: '100%',
          height: '100%',
          position: 'relative',
          bgcolor: 'rgba(0, 0, 0, 0.8)',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: '100%', p: '0 15px', position: 'absolute', top: '15px', zIndex: 1 }}
      >
        {isStreamer && (
          <>
            <Stack direction="row" spacing={1}>
              <Tooltip title={<span>{t('component.group.medicalVideo.changeCameraButton.tooltip')}</span>}>
                <Fab color="primary" onClick={toggleDeviceSelectionDialog} size="small">
                  <CameraswitchOutlinedIcon />
                </Fab>
              </Tooltip>
              {isScreenSharing ? (
                <Tooltip title={<span>{t('component.group.medicalVideo.stopShareScreenButton.tooltip')}</span>}>
                  <Fab color="primary" onClick={handleStopShareScreen} size="small">
                    <StopScreenShareOutlinedIcon />
                  </Fab>
                </Tooltip>
              ) : (
                <Tooltip title={<span>{t('component.group.medicalVideo.shareScreenButton.tooltip')}</span>}>
                  <Fab color="primary" onClick={handleStartShareScreen} size="small">
                    <ScreenShareOutlinedIcon />
                  </Fab>
                </Tooltip>
              )}
              {!disableConnectionIndicator && <CnxIndicator />}
            </Stack>
            {!!selectedDevice && (
              <IconButton {...bindTrigger(videoParamsPopupState)}>
                <MoreVertIcon htmlColor="#FFF" />
              </IconButton>
            )}
          </>
        )}
        {videoRecorder.isRecording && (
          <Box
            sx={{
              backgroundColor: colors.red500,
              color: '#FFF',
              p: '5px 10px',
              borderRadius: '4px',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            {dayjs.duration(videoRecorder.recordingTime, 'seconds').format('mm:ss')}
          </Box>
        )}
      </Stack>
      {renderVideo()}
      {!!stream && !!stream.getVideoTracks()[0] && (
        <Stack
          direction="row"
          justifyContent="center"
          spacing={3}
          sx={{
            width: '100%',
            position: 'absolute',
            bottom: {
              xs: '80px',
              sm: '15px',
            },
          }}
        >
          <ButtonGroup>
            <Tooltip title={<span>{t('component.group.medicalVideo.screenshotButton.tooltip')}</span>}>
              <Button onClick={onScreenshot} variant="neutral">
                <Badge
                  badgeContent={screenshotCount || getFileCount(consultation, ['png', 'jpg', 'jpeg'])}
                  color="primary"
                  sx={{ position: 'absolute', right: '14px', top: '2px' }}
                ></Badge>
                <CameraIcon />
              </Button>
            </Tooltip>
            {!videoRecorder.isRecording ? (
              <Tooltip title={<span>{t('component.group.medicalVideo.startRecordingButton.tooltip')}</span>}>
                <Button onClick={handleStartRecording} variant="neutral">
                  <Badge badgeContent={videoRecordingCount || getFileCount(consultation, ['webm'])} color="primary" sx={{ position: 'absolute', right: '14px', top: '2px' }}></Badge>
                  <RadioButtonCheckedIcon sx={{ color: colors.red500 }} />
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title={<span>{t('component.group.medicalVideo.stopRecordingButton.tooltip')}</span>}>
                <Button onClick={handleStopRecording} variant="neutral">
                  <StopCircleOutlinedIcon sx={{ color: colors.red500 }} />
                </Button>
              </Tooltip>
            )}
            {/* <Tooltip title={<span>{t('component.group.medicalVideo.freezeVideoButton.tooltip')}</span>}>
              <Button onClick={handleFreezeVideo} variant="neutral">
                <PausePresentationOutlinedIcon />
              </Button>
            </Tooltip>
            <Tooltip title={<span>{t('component.group.medicalVideo.stopVideoButton.tooltip')}</span>}>
              <Button onClick={handleStopVideo} variant="neutral">
                <CancelPresentationOutlinedIcon />
              </Button>
            </Tooltip> */}
          </ButtonGroup>
        </Stack>
      )}
      <DeviceSelectionDialog
        devices={videoInputDevices}
        onClose={toggleDeviceSelectionDialog}
        onSelectDevice={handleSelectDevice}
        open={deviceSelectionDialogOpen}
        showAudioInput={false}
        showAudioOutput={false}
        context="medicalVideo"
      />
      {/* {videoFreezeDialogOpen && (
        <VideoFreezeDialog
          onClose={toggleVideoFreezeDialog}
          onSavePhoto={onSaveFreezedPhoto}
          onSaveVideo={onSaveFreezedVideo}
          open
          videoSrcBlob={videoFreezeSrcBlob}
        />
      )} */}
      <Menu
        {...bindMenu(videoParamsPopupState)}
        disableAutoFocusItem
        variant="menu"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem {...bindTrigger(videoQualityPopupState)}>
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText>{t('component.group.medicalVideo.videoParams.quality.label')}</ListItemText>
        </MenuItem>
      </Menu>
      <Menu
        {...bindMenu(videoQualityPopupState)}
        disableAutoFocusItem
        variant="menu"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            maxHeight: '300px',
          },
        }}
      >
        {!selectedDevice?.resolutions?.length ? (
          <MenuItem disabled>{t('component.group.medicalVideo.videoParams.quality.resolution.noResolution')}</MenuItem>
        ) : (
          _.map(selectedDevice?.resolutions, (resolution) => (
            <MenuItem
              key={resolution}
              onClick={() => {
                setSelectedResolution(resolution)
                onSelectResolution?.(selectedDevice.id, resolution)
                bindMenu(videoQualityPopupState).onClose()
                bindMenu(videoParamsPopupState).onClose()
              }}
              selected={selectedResolution === resolution}
            >
              {resolution}
            </MenuItem>
          ))
        )}
      </Menu>
    </Box>
  )
})

export default MedicalVideo
